import React from 'react';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { alpha } from '@mui/system';
import { useTheme } from '../../../../../hooks';
import { usePopperProps } from './hooks';
import { DateTimePickerProps } from './model';

interface DateTimePickerContentProps {
  anchorEl: HTMLDivElement | null;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  disableFuture?: boolean;
  disablePast?: boolean;
  fieldComponent: React.ComponentType;
  innerValue: Date | null;
  isOpen: boolean;
  onAccept: (newValue: Date | null) => void;
  onChange: (newValue: Date | null) => void;
  onClose: () => void;
  onMonthChange?: (month: Date) => void;
  onOpen: () => void;
  pickerType: DateTimePickerProps['pickerType'];
  popperProps: ReturnType<typeof usePopperProps>;
  shouldDisableDate?: (date: Date) => boolean;
  theme: ReturnType<typeof useTheme>;
}

/**
 * Wrapper component for MUI DatePicker with settings
 */
export const DateTimePickerContent: React.FC<DateTimePickerContentProps> = ({
  pickerType,
  innerValue,
  isOpen,
  fieldComponent,
  popperProps,
  theme,
  onAccept,
  onChange,
  onClose,
  onOpen,
  onMonthChange,
  shouldDisableDate,
  disablePast,
  disableFuture,
}) => {
  const Picker = React.useMemo(() => {
    switch (pickerType) {
      case 'dateTime':
        return MuiDateTimePicker;
      case 'time':
        return MuiTimePicker;
      case 'date':
      default:
        return MuiDatePicker;
    }
  }, [pickerType]);

  const paperSx = React.useMemo(
    () => ({
      borderRadius: `${theme.borderRadius.normal}px`,
      backgroundColor: 'white',
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      boxShadow: `0px 4px 16px ${alpha(theme.palette.black as string, 0.13)}`,
    }),
    [theme],
  );

  return (
    <Picker
      // @ts-expect-error - PaperProps type mismatch in MUI DatePicker
      PaperProps={{ sx: paperSx }}
      disableFuture={disableFuture}
      disablePast={disablePast}
      label="Basic date picker"
      onAccept={onAccept}
      onChange={onChange}
      onClose={onClose}
      onMonthChange={onMonthChange}
      onOpen={onOpen}
      open={isOpen}
      shouldDisableDate={shouldDisableDate}
      slotProps={{ popper: popperProps as never }}
      slots={{ field: fieldComponent }}
      value={innerValue}
    />
  );
};
